import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Currently highlighted item identifier
  highlightedItemId: null,
  // Type of highlight (hover or click)
  highlightType: null, 
  // Source of the highlight (invoice or cart-composer)
  highlightSource: null,
  // Additional metadata for complex matching scenarios
  highlightMetadata: null,
  // Flag to indicate when to scroll to the highlighted item
  shouldScrollToHighlight: false,
};

export const highlightSlice = createSlice({
  name: 'highlight',
  initialState,
  reducers: {
    // Set a highlighted item
    setHighlightedItem: (state, action) => {
      const { itemId, type = 'hover', source, metadata = null, shouldScroll = true } = action.payload;
      state.highlightedItemId = itemId;
      state.highlightType = type;
      state.highlightSource = source;
      state.highlightMetadata = metadata;
      // Set the scroll flag based on the action parameter or default to true
      state.shouldScrollToHighlight = shouldScroll;
    },
    
    // Clear highlighted item
    clearHighlightedItem: (state) => {
      state.highlightedItemId = null;
      state.highlightType = null;
      state.highlightSource = null;
      state.highlightMetadata = null;
      state.shouldScrollToHighlight = false;
    },
    
    // Reset the scroll flag after scrolling is complete
    resetScrollFlag: (state) => {
      state.shouldScrollToHighlight = false;
    },
  },
});

// Export actions
export const { 
  setHighlightedItem,
  clearHighlightedItem,
  resetScrollFlag,
} = highlightSlice.actions;

// Selectors
export const selectHighlightedItem = (state) => state.highlight.highlightedItemId;
export const selectHighlightType = (state) => state.highlight.highlightType;
export const selectHighlightSource = (state) => state.highlight.highlightSource;
export const selectHighlightMetadata = (state) => state.highlight.highlightMetadata;
export const selectShouldScrollToHighlight = (state) => state.highlight.shouldScrollToHighlight;

// Helper selector to check if a specific item matches the highlighted item
export const selectIsItemHighlighted = (state, itemId) => {
  return state.highlight.highlightedItemId === itemId;
};

// Selectors to identify group items that should be highlighted
export const selectShouldHighlightGroup = (state, item) => {
  const { highlightedItemId, highlightMetadata } = state.highlight;
  
  if (!highlightedItemId) return false;
  
  // Direct match of item ID
  if (highlightedItemId === item.line_item_id) return true;
  
  // Match items in the same style/color group
  if (highlightMetadata && 
      highlightMetadata.dgi_style_sku === item.dgi_style_sku && 
      highlightMetadata.master_color === item.master_color) {
    
    // If size is also specified, only highlight matching size
    if (highlightMetadata.size) {
      return highlightMetadata.size === item.size;
    }
    
    return true;
  }
  
  return false;
};

export default highlightSlice.reducer;